import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expenseService, ExpenseReportFilters } from '../expenseService';
import {
  CreateExpenseDto,
  ExpenseFilters,
  PayExpenseDto,
} from '../../../types/expense';

// ── Expenses ────────────────────────────────────────────────────────────

export function useExpenses(filters?: ExpenseFilters) {
  return useQuery({
    queryKey: ['expenses', filters],
    queryFn: () => expenseService.getExpenses(filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useExpenseSummary(filters?: ExpenseFilters) {
  return useQuery({
    queryKey: ['expenses', 'summary', filters],
    queryFn: () => expenseService.getSummary(filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useExpense(id?: string) {
  return useQuery({
    queryKey: ['expenses', 'detail', id],
    queryFn: () => expenseService.getExpense(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** Grouped "Expenses by Category" report (auto-refetches when the filters change). */
export function useExpenseCategoryReport(filters: ExpenseReportFilters) {
  return useQuery({
    queryKey: ['expenses', 'by-category', filters],
    queryFn: () => expenseService.getCategoryReport(filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

/** Generate + download the grouped expense report (pdf | excel | csv). */
export function useExportExpenseCategoryReport() {
  return useMutation({
    mutationFn: async ({ format, filters }: { format: string; filters: ExpenseReportFilters }) => {
      const { blob, filename } = await expenseService.exportCategoryReport(format, filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return filename;
    },
    meta: { successMessage: 'Report downloaded' },
  });
}

function invalidateLedger(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['expenses'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['cashbook'] });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateExpenseDto) => expenseService.createExpense(data),
    meta: { successMessage: 'Expense recorded' },
    onSuccess: () => invalidateLedger(queryClient),
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateExpenseDto> }) =>
      expenseService.updateExpense(id, data),
    meta: { successMessage: 'Expense updated' },
    onSuccess: () => invalidateLedger(queryClient),
  });
}

export function usePayExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PayExpenseDto }) =>
      expenseService.payExpense(id, data),
    meta: { successMessage: 'Expense marked as paid' },
    onSuccess: () => invalidateLedger(queryClient),
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => expenseService.deleteExpense(id),
    meta: { successMessage: 'Expense deleted' },
    onSuccess: () => invalidateLedger(queryClient),
  });
}

export function useBulkDeleteExpenses() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => expenseService.bulkDeleteExpenses(ids),
    meta: { hideToast: true },
    onSuccess: () => invalidateLedger(queryClient),
  });
}

/** Fetch an expense voucher as an object URL (caller opens/downloads it). */
export function useExpenseVoucher() {
  return useMutation({
    mutationFn: (id: string) => expenseService.getVoucherUrl(id),
    meta: { hideToast: true },
  });
}
